import { components } from "@libertify/api-types/media";
import {
  HttpError,
  useCustom,
  useGetLocale,
  useTranslate,
} from "@refinedev/core";
import { Flex, Spin } from "antd";
import { useOrganization } from "hooks/useOrganization";
import { VoiceProfileResponse } from "pages/media/types";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AvatarVoicePreview } from "./AvatarVoicePreview";
import { CascadingFilters } from "./CascadingFilters";
import { CloneButton } from "./CloneButton";
import {
  categories,
  getVoiceAccentOptionsForLanguage,
  getVoiceLanguageCodes,
  getVoiceLocaleOptionsForLanguage,
} from "./helpers";
import { useInfiniteVoiceFaceProfiles } from "./hooks/useInfiniteVoiceFaceProfiles";
import { InfiniteProfileList } from "./InfiniteProfileList";
import {
  ActiveTabName,
  VoiceAndFaceContainerProps,
  VoiceFilterOptions,
  VOICE_ACCENT_FILTER_KEY,
  VOICE_LANGUAGE_FILTER_KEY,
  VOICE_LOCALE_FILTER_KEY,
} from "./types";
import VoiceAndFaceSelector from "./VoiceAndFaceSelector";

type PremadeVoiceFiltersResponse =
  components["schemas"]["PremadeVoiceFiltersResponse"];

type VoiceFilterData = {
  tags: VoiceFilterOptions;
  languages: PremadeVoiceFiltersResponse["languages"];
};

const VoiceSelector = ({
  selectedVoice,
  cloneVoiceResponse,
  setClonedVoice,
  setSelectedVoice,
  setCurrentModal,
  clonedVoice,
  isLoadingExistingContact,
  isNewAvatar,
  voiceOnly,
  style,
}: VoiceAndFaceContainerProps) => {
  const t = useTranslate();
  const [activeKey, setActiveKey] = useState<ActiveTabName | null>(null);
  const [currentlyPlayedVoice, setCurrentlyPlayedVoice] = useState<
    VoiceProfileResponse | undefined
  >(undefined);

  const { id: recordId } = useParams();
  const { organizationPreferences } = useOrganization({});
  const locale = useGetLocale()() ?? "en";

  const [initialSelectedVoice, setInitialSelectedVoice] = useState<
    VoiceProfileResponse | undefined
  >(undefined);
  const activeKeyFromSelectedVoice =
    selectedVoice?.category === "Cloned"
      ? ActiveTabName.Custom
      : ActiveTabName.Premade;
  const currentActiveKey = activeKey ?? activeKeyFromSelectedVoice;

  const [voiceFilters, setVoiceFilters] = useState<Record<string, string[]>>(
    {}
  );
  if (
    ((!initialSelectedVoice && selectedVoice?.id) ||
      (cloneVoiceResponse && selectedVoice?.id)) &&
    initialSelectedVoice?.id !== selectedVoice.id
  ) {
    setInitialSelectedVoice(selectedVoice);
  }

  const { data: filterOptions, isLoading: isLoadingFilterOptions } = useCustom<
    PremadeVoiceFiltersResponse,
    HttpError,
    unknown,
    unknown,
    VoiceFilterData
  >({
    url: "media/voice_profiles/filters",
    method: "get",
    queryOptions: {
      enabled: currentActiveKey === ActiveTabName.Premade,
      select: (data) => {
        return {
          total: data.data.tag_groups.length,
          data: {
            tags: data.data.tag_groups.reduce((acc, { key, values }) => {
              acc[key] = values;
              return acc;
            }, {} as VoiceFilterOptions),
            languages: data.data.languages,
          },
        };
      },
    },
  });

  const defaultLanguageCode = useMemo(() => {
    if (!isNewAvatar) return undefined;
    const orgLang: string | undefined = organizationPreferences?.language;
    const availableLangs = filterOptions?.data.languages;
    if (!orgLang || !availableLangs?.length) return undefined;
    const codes = getVoiceLanguageCodes(availableLangs);
    const base = orgLang.split("-")[0];
    return codes.find((c) => c === orgLang) ?? codes.find((c) => c === base);
  }, [
    isNewAvatar,
    organizationPreferences?.language,
    filterOptions?.data.languages,
  ]);

  // One-shot init: seed voiceFilters with the org's default language as soon as
  // it becomes available. After this, voiceFilters is the single source of truth,
  // so clearing/changing the language filter works normally without the default
  // re-applying. Cannot be done at render time (org prefs are async) nor as
  // derived state (must distinguish "not yet set" from "user cleared it").
  useEffect(() => {
    if (!defaultLanguageCode) return;
    setVoiceFilters((prev) =>
      prev[VOICE_LANGUAGE_FILTER_KEY]
        ? prev
        : { ...prev, [VOICE_LANGUAGE_FILTER_KEY]: [defaultLanguageCode] }
    );
  }, [defaultLanguageCode]);

  const selectedLanguageCode = voiceFilters[VOICE_LANGUAGE_FILTER_KEY]?.[0];

  const voiceFilterCascadingOptions = useMemo(() => {
    const tags = filterOptions?.data.tags;
    const langs = filterOptions?.data.languages;
    if (!tags && !langs?.length) {
      return undefined as VoiceFilterOptions | undefined;
    }
    const merged: VoiceFilterOptions = {};
    if (langs?.length) {
      merged[VOICE_LANGUAGE_FILTER_KEY] = getVoiceLanguageCodes(langs);
    }
    if (selectedLanguageCode && langs?.length) {
      const accents = getVoiceAccentOptionsForLanguage(
        langs,
        selectedLanguageCode
      );
      if (accents.length > 0) {
        merged[VOICE_ACCENT_FILTER_KEY] = accents;
      }
      const locales = getVoiceLocaleOptionsForLanguage(
        langs,
        selectedLanguageCode
      );
      if (locales.length > 0) {
        merged[VOICE_LOCALE_FILTER_KEY] = locales;
      }
    }
    Object.assign(merged, tags ?? {});
    return merged;
  }, [
    filterOptions?.data.tags,
    filterOptions?.data.languages,
    selectedLanguageCode,
  ]);

  const voiceFilterOptionLabels = useMemo(() => {
    const codes = voiceFilterCascadingOptions?.[VOICE_LANGUAGE_FILTER_KEY];
    if (!codes?.length) return undefined;
    const displayNames = new Intl.DisplayNames([locale], { type: "language" });
    const labels: Record<string, string> = {};
    for (const code of codes) {
      labels[code] = displayNames.of(code) ?? code;
    }
    return { [VOICE_LANGUAGE_FILTER_KEY]: labels };
  }, [voiceFilterCascadingOptions, locale]);

  // Use cloned voice if it exists, otherwise use initial selected voice for insertion
  // The initial selected voice should always stay at the beginning
  const previewProfile = clonedVoice || initialSelectedVoice || selectedVoice;
  const initialVoiceCategory = initialSelectedVoice?.category;
  const initialVoiceHasSameCategory =
    initialVoiceCategory &&
    categories[initialVoiceCategory] === currentActiveKey;

  // Always insert at the beginning if:
  // 1. It's a newly cloned voice (cloneVoiceResponse exists), OR
  // 2. We have an initial selected voice (saved voice) that matches the current tab category
  //    and there are no filters applied
  // The initial voice will always stay at the beginning even if user selects a different voice
  const shouldInsertSelected =
    !!cloneVoiceResponse ||
    (!!initialSelectedVoice &&
      !!recordId &&
      initialVoiceHasSameCategory &&
      Object.keys(voiceFilters).length === 0);

  const {
    profiles: items,
    isLoading,
    hasMore,
    loadMoreData,
  } = useInfiniteVoiceFaceProfiles<VoiceProfileResponse>({
    resourceType: currentActiveKey,
    previewProfile,
    pageSize: voiceOnly ? 15 : 10,
    type: "voice",
    filters:
      currentActiveKey === ActiveTabName.Premade ? voiceFilters : undefined,
    insertSelected: shouldInsertSelected,
  });

  const handleClearAllFilters = () => {
    setVoiceFilters({});
  };

  const handleFiltersChange = (key: string, value: string) => {
    setVoiceFilters((prev) => {
      if (key === VOICE_LANGUAGE_FILTER_KEY) {
        const current = prev[VOICE_LANGUAGE_FILTER_KEY] ?? [];
        if (current.includes(value)) {
          const {
            [VOICE_LANGUAGE_FILTER_KEY]: _l,
            [VOICE_ACCENT_FILTER_KEY]: _a,
            [VOICE_LOCALE_FILTER_KEY]: _lo,
            ...rest
          } = prev;
          return rest;
        }
        const {
          [VOICE_ACCENT_FILTER_KEY]: _a,
          [VOICE_LOCALE_FILTER_KEY]: _lo,
          ...rest
        } = prev;
        return { ...rest, [VOICE_LANGUAGE_FILTER_KEY]: [value] };
      }

      if (key === VOICE_ACCENT_FILTER_KEY) {
        const current = prev[VOICE_ACCENT_FILTER_KEY] ?? [];
        if (current.includes(value)) {
          const { [VOICE_ACCENT_FILTER_KEY]: _a, ...rest } = prev;
          return rest;
        }
        return { ...prev, [VOICE_ACCENT_FILTER_KEY]: [value] };
      }

      const currentValues = prev[key] ?? [];
      const exists = currentValues.includes(value);
      const updatedValues = exists
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      const newFilters = { ...prev };
      if (updatedValues.length > 0) {
        newFilters[key] = updatedValues;
      } else {
        delete newFilters[key];
      }
      return newFilters;
    });
  };

  const renderItem = (
    item: VoiceProfileResponse,
    index: string | number,
    setSelected: (voice: VoiceProfileResponse) => void,
    selectedItem?: Partial<VoiceProfileResponse>,
    clonedItem?: VoiceProfileResponse
  ) => {
    return (
      <div style={{ paddingTop: 5, paddingRight: 5, maxWidth: 320 }}>
        <AvatarVoicePreview
          voice={item}
          onClick={() => setSelected(item)}
          className={`voice-${index}`}
          isSelected={item.id === (selectedItem?.id ?? clonedItem?.id)}
          currentlyPlayedVoice={currentlyPlayedVoice}
          setCurrentlyPlayedVoice={setCurrentlyPlayedVoice}
          language={
            currentActiveKey === ActiveTabName.Premade
              ? selectedLanguageCode
              : undefined
          }
        />
      </div>
    );
  };

  return (
    <Flex vertical style={{ ...style, height: "100%" }}>
      <Flex vertical style={{ height: "100%" }} gap={16}>
        {isLoadingExistingContact && !!recordId ? (
          <Flex style={{ margin: "auto" }} justify="center" align="center">
            <Spin size="large" />
          </Flex>
        ) : (
          <VoiceAndFaceSelector
            type="voice"
            onTabClick={(key) => {
              setActiveKey(key);
            }}
            activeKey={currentActiveKey}
            filters={
              isLoadingFilterOptions ? (
                <Spin spinning={true} />
              ) : (
                <CascadingFilters
                  type="voice"
                  onChange={handleFiltersChange}
                  onClearAll={handleClearAllFilters}
                  filters={voiceFilters}
                  options={voiceFilterCascadingOptions ?? {}}
                  optionLabels={voiceFilterOptionLabels}
                  itemsCount={items?.length ?? 0}
                  label={t(
                    "components.ContactWithPresenter.VoiceFilter.filters"
                  )}
                  loading={isLoadingFilterOptions}
                />
              )
            }
            premade={
              <InfiniteProfileList<VoiceProfileResponse, VoiceProfileResponse>
                type="voice"
                profiles={items}
                isLoading={isLoading}
                selected={selectedVoice}
                clonedResponse={cloneVoiceResponse}
                setCloned={(cloned) => setClonedVoice?.(cloned)}
                setSelected={(selected) => setSelectedVoice?.(selected ?? null)}
                renderItem={renderItem}
                hasMore={hasMore}
                loadMoreData={loadMoreData}
                isNewAvatar={isNewAvatar}
              />
            }
            custom={
              <InfiniteProfileList
                type="voice"
                profiles={items}
                isLoading={isLoading}
                selected={selectedVoice}
                clonedResponse={cloneVoiceResponse}
                setCloned={(cloned) => setClonedVoice?.(cloned)}
                setSelected={(selected) => setSelectedVoice?.(selected ?? null)}
                cloneButton={
                  items.length === 0 && !isLoading ? (
                    <CloneButton
                      type="primary"
                      title={t(
                        "components.ContactWithPresenter.VoiceSelector.cloneANew"
                      )}
                      onClick={() => {
                        setCurrentModal("voice");
                      }}
                    />
                  ) : undefined
                }
                renderItem={renderItem}
                hasMore={hasMore}
                loadMoreData={loadMoreData}
                isNewAvatar={isNewAvatar}
              />
            }
          />
        )}
        {currentActiveKey === ActiveTabName.Custom &&
          items.length > 0 &&
          !isLoading && (
            <CloneButton
              title={t(
                "components.ContactWithPresenter.VoiceSelector.cloneANew"
              )}
              onClick={() => {
                setCurrentModal("voice");
              }}
            />
          )}
      </Flex>
    </Flex>
  );
};

export default VoiceSelector;
